import { RealtimeModes as modes } from "mobility-toolbox-js/api";
import { touchOnly } from "ol/events/condition";
import { unByKey } from "ol/Observable";
import { memo, useEffect } from "react";

import { TRACK_EVENT_CATEGORY_MAP } from "../constants";
import {
  setIsFollowingVehicle,
  setSelectedNotificationsById,
  setSelectedVehicles,
  setStation,
  trackEvent,
} from "../model/actions";
import getStationFromSchematicLayer from "../utils/getStationFromSchematicLayer";
import getStationFromTopographicLayer from "../utils/getStationFromTopographicLayer";
import getStationFromUic from "../utils/getStationFromUic";
import useAppDispatch from "../utils/hooks/useAppDispatch";
import useAppSelector from "../utils/hooks/useAppSelector";

import { eventLayer } from "./EventLayer";

import type {
  RealtimeLine,
  RealtimeStationproperties,
  RealtimeTrainId,
  RealtimeTrajectoryProperties,
} from "mobility-toolbox-js/types";
import type { Feature } from "ol";
import type { Geometry } from "ol/geom";
import type MapBrowserEvent from "ol/MapBrowserEvent";

type StationFeature = Feature<Geometry, RealtimeStationproperties>;

interface MapClickListenerProps {
  // Called right before a vehicle is selected via a single click, so LiveMap
  // can force a full trajectory re-fetch for the newly (re-)selected vehicle.
  onBeforeSelectVehicles?: () => void;
}

/**
 * Registers the map's "pointermove" and "singleclick" listeners used for
 * station/vehicle hover and selection. Extracted from LiveMap.
 */
function MapClickListener({ onBeforeSelectVehicles }: MapClickListenerProps) {
  const dispatch = useAppDispatch();
  const map = useAppSelector((state) => state.map);
  const config = useAppSelector((state) => state.config);
  const mode = useAppSelector((state) => state.mode);
  const station = useAppSelector((state) => state.station);
  const stationsIds = useAppSelector((state) => state.stationsIds);
  const backgroundLayer = useAppSelector((state) => state.backgroundLayer);
  const topographicBgLayer = useAppSelector(
    (state) => state.topographicBgLayer,
  );
  const schematicBgLayer = useAppSelector((state) => state.schematicBgLayer);
  const notificationsLayer = useAppSelector(
    (state) => state.notificationsLayer,
  );
  const notificationsIconsLayer = useAppSelector(
    (state) => state.notificationsIconsLayer,
  );
  const realtimeLayer = useAppSelector((state) => state.realtimeLayer);
  const stationsLayer = useAppSelector((state) => state.stationsLayer);
  const noRouteInfo = useAppSelector((state) => state.noRouteInfo);

  useEffect(() => {
    if (!map || !config.userInteractions) {
      return undefined;
    }

    const getStationAtPointerEvt = async (
      evt: MapBrowserEvent,
    ): Promise<null | StationFeature | undefined> => {
      // Search fo a station in the StationsLayer
      let [stationOver] = map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer === stationsLayer,
      }) as StationFeature[];

      // If no stations markers found we search in the mapbox layers,
      // the uic to find the correct station.
      if (!stationOver) {
        let uic;

        if (
          mode === modes.SCHEMATIC &&
          backgroundLayer?.mbMap?.isStyleLoaded()
        ) {
          stationOver = (await getStationFromSchematicLayer(
            evt.pixel,
            backgroundLayer,
          )) as StationFeature;

          uic = stationOver?.get("ibnr") as string | undefined;
        } else if (
          mode === modes.TOPOGRAPHIC &&
          backgroundLayer?.mbMap?.isStyleLoaded()
        ) {
          stationOver = (await getStationFromTopographicLayer(
            evt.pixel,
            backgroundLayer,
            stationsIds,
          )) as StationFeature;
          uic = stationOver?.get("uic_ref") as string | undefined;
        }

        if (uic) {
          stationOver = getStationFromUic(
            stationsLayer.getSource()?.getFeatures(),
            uic,
          ) as StationFeature;
        }
      }
      if (
        stationOver &&
        station &&
        stationOver.get("uic") === station.get("uic")
      ) {
        // ignore station already selected.
        return null;
      }

      return stationOver;
    };

    const getNotificationsAtPointerEvt = (evt: MapBrowserEvent) => {
      const features = map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer === notificationsLayer,
      });

      const icons = map.getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer === notificationsIconsLayer,
      });

      return [...features, ...icons].map(
        (feature) => feature.get("situationId") as string,
      );
    };

    const getTralisEventsAtPointerEvt = (evt: MapBrowserEvent) => {
      return map
        .getFeaturesAtPixel(evt.pixel, {
          layerFilter: (layer) => layer === eventLayer,
        })
        .filter((feature) => !!feature.get("onClick"));
    };

    const onPointerMove = async (evt: MapBrowserEvent) => {
      if (!realtimeLayer) {
        return;
      }

      realtimeLayer.hoverVehicleId = null as unknown as RealtimeTrainId;
      const view = evt.map.getView();
      if (touchOnly(evt) || view.getAnimating() || view.getInteracting()) {
        return;
      }

      let features: unknown[] = [];
      if (stationsLayer?.getVisible()) {
        const stationOver = await getStationAtPointerEvt(evt);
        if (stationOver) {
          features.push(stationOver);
        }
      }

      if (
        !noRouteInfo &&
        (realtimeLayer as unknown as { visible?: boolean })?.visible &&
        !features.length
      ) {
        const vehicles = map
          .getFeaturesAtPixel(evt.pixel, {
            hitTolerance: 10,
            layerFilter: (layer) => layer === realtimeLayer,
          })
          .filter(
            // SBAHNMW-891, HACK:
            // Vehicle with no train number returns a stop sequence in the past,
            // so we exclude them from click selection.
            // This should be fix in backend.
            (feature) => !!(feature.get("line") && feature.get("train_number")),
          )
          .map(
            (feature) =>
              feature.getProperties() as RealtimeTrajectoryProperties,
          );

        features = vehicles;

        if (features.length) {
          realtimeLayer.hoverVehicleId = vehicles[0].train_id;
        }
      }

      if (!features.length) {
        features = getNotificationsAtPointerEvt(evt);
      }

      if (!features.length) {
        features = getTralisEventsAtPointerEvt(evt);
      }

      const target = map.getTargetElement();
      if (target) {
        target.style.cursor = features.length ? "pointer" : "default";
      }
    };

    const onSingleClick = async (evt: MapBrowserEvent) => {
      if (!realtimeLayer) {
        return;
      }

      realtimeLayer.hoverVehicleId = null as unknown as RealtimeTrainId;

      if (stationsLayer?.getVisible()) {
        const stationOver = await getStationAtPointerEvt(evt);

        // if a station is selected, not a line.
        if (stationOver?.get("uic")) {
          dispatch(setIsFollowingVehicle(false));
          dispatch(setSelectedVehicles());
          dispatch(setStation(stationOver));
          dispatch(
            trackEvent(
              TRACK_EVENT_CATEGORY_MAP,
              "selectStation",
              stationOver.get("name") as string,
            ),
          );
          dispatch(setSelectedNotificationsById([]));
          return;
        }
      }

      if (
        !noRouteInfo &&
        (realtimeLayer as unknown as { visible?: boolean })?.visible
      ) {
        const vehicles = map
          .getFeaturesAtPixel(evt.pixel, {
            hitTolerance: 10,
            layerFilter: (layer) => layer === realtimeLayer,
          })
          .filter(
            // SBAHNMW-891, HACK:
            // Vehicle with no train number returns a stop sequence in the past,
            // so we exclude them from click selection.
            // This should be fix in backend.
            (feature) => !!(feature.get("line") && feature.get("train_number")),
          )
          .map(
            (feature) =>
              feature.getProperties() as RealtimeTrajectoryProperties,
          );

        onBeforeSelectVehicles?.();
        dispatch(setSelectedVehicles(vehicles));
        dispatch(setSelectedNotificationsById([]));
        if (vehicles.length > 0) {
          const lineNames = vehicles
            .map((v) => (v.line as RealtimeLine | undefined)?.name)
            .join(", ");
          dispatch(
            trackEvent(TRACK_EVENT_CATEGORY_MAP, "selectVehicle", lineNames),
          );
          return;
        }
      }

      const notifications = getNotificationsAtPointerEvt(evt);
      dispatch(setSelectedNotificationsById(notifications));
      dispatch(setStation());
      dispatch(setSelectedVehicles());
    };

    const pointerMoveKey = map.on("pointermove", onPointerMove);
    const singleClickKey = map.on("singleclick", onSingleClick);

    return () => {
      unByKey(pointerMoveKey);
      unByKey(singleClickKey);
    };
  }, [
    backgroundLayer,
    config,
    dispatch,
    map,
    mode,
    noRouteInfo,
    notificationsIconsLayer,
    notificationsLayer,
    onBeforeSelectVehicles,
    realtimeLayer,
    schematicBgLayer,
    station,
    stationsIds,
    stationsLayer,
    topographicBgLayer,
  ]);

  return null;
}

export default memo(MapClickListener);
